import ReactMarkdown from 'react-markdown';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import { Components } from 'react-markdown';

const markdownComponents: Components = {
	h1: ({ ...props }) => (
		<h1
			className="mb-8 text-left text-2xl font-bold leading-tight tracking-normal text-gray-900 md:text-3xl"
			{...props}
		/>
	),
	h2: ({ ...props }) => (
		<h2 className="pt-6 text-2xl font-semibold text-gray-800" {...props} />
	),
	h3: ({ ...props }) => (
		<h3 className="pt-4 text-xl font-semibold text-gray-800" {...props} />
	),
	p: ({ ...props }) => (
		<p className="whitespace-pre-line text-zinc-600" {...props} />
	),
	ul: ({ ...props }) => (
		<ul className="list-disc space-y-2 pl-5" {...props} />
	),
	ol: ({ ...props }) => (
		<ol className="list-decimal space-y-2 pl-5" {...props} />
	),
	li: ({ ...props }) => <li className="text-zinc-600" {...props} />,
	hr: () => <div className="my-2 h-px w-full bg-transparent" aria-hidden />,
	strong: ({ ...props }) => (
		<strong className="font-semibold text-gray-800" {...props} />
	),
	em: ({ ...props }) => <em className="italic" {...props} />,
	code: ({ ...props }) => (
		<code
			className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-[0.92em] text-zinc-800"
			{...props}
		/>
	),
	blockquote: ({ ...props }) => (
		<blockquote
			className="border-l-4 border-zinc-200 pl-4 italic text-zinc-500"
			{...props}
		/>
	),
	table: ({ ...props }) => (
		<div className="overflow-x-auto py-1">
			<table
				className="w-full min-w-[720px] border-collapse border border-zinc-200 text-left text-sm"
				{...props}
			/>
		</div>
	),
	thead: ({ ...props }) => <thead className="bg-zinc-100" {...props} />,
	tbody: ({ ...props }) => <tbody {...props} />,
	tr: ({ ...props }) => <tr className="align-top" {...props} />,
	th: ({ ...props }) => (
		<th
			className="border border-zinc-200 px-3 py-2 font-semibold text-zinc-700"
			{...props}
		/>
	),
	td: ({ ...props }) => (
		<td
			className="border border-zinc-200 px-3 py-2 text-zinc-600"
			{...props}
		/>
	),
	a: ({ ...props }) => (
		<a
			className="text-brand-pink underline transition-colors hover:text-brand-pink/80"
			target="_blank"
			rel="noopener noreferrer"
			{...props}
		/>
	),
};

const formatDate = (iso: string) => {
	const d = new Date(iso);
	return d.toLocaleDateString('pt-BR', {
		day: '2-digit',
		month: 'long',
		year: 'numeric',
	});
};

interface BlogPostContentProps {
	title: string;
	content: string;
	banner_url: string;
	created_at?: string;
	onBack?: () => void;
	isPreview?: boolean;
}

const BlogPostContent = ({
	title,
	content,
	banner_url,
	created_at,
	onBack,
	isPreview = false,
}: BlogPostContentProps) => {
	const handleBack = () => {
		if (isPreview) {
			toast.info(
				'O botão "Voltar ao Blog" não funciona em modo de preview.',
			);
			return;
		}
		onBack?.();
	};

	return (
		<>
			{banner_url && (
				<div className="h-72 w-full overflow-hidden bg-zinc-100 md:h-96">
					<img
						src={banner_url}
						alt={title}
						className="h-full w-full object-cover"
						onError={(e) => {
							(
								e.target as HTMLImageElement
							).parentElement!.style.display = 'none';
						}}
					/>
				</div>
			)}

			<article className="mx-auto max-w-3xl px-6 py-12">
				<button
					type="button"
					onClick={handleBack}
					className="mb-8 inline-flex items-center gap-2 text-sm text-zinc-400 transition hover:text-zinc-700"
				>
					<ArrowLeft className="h-4 w-4" />
					Voltar ao Blog
				</button>

				{created_at && (
					<p className="text-sm text-zinc-400">
						{formatDate(created_at)}
					</p>
				)}
				<h1 className="mt-2 text-3xl font-bold leading-tight text-zinc-900 md:text-4xl">
					{title || (
						<span className="italic text-zinc-300">Sem título</span>
					)}
				</h1>

				<div className="mt-10 space-y-6 text-justify text-zinc-600">
					{content ? (
						<ReactMarkdown components={markdownComponents}>
							{content}
						</ReactMarkdown>
					) : (
						<p className="italic text-zinc-300">
							Nenhum conteúdo para exibir.
						</p>
					)}
				</div>
			</article>
		</>
	);
};

export { BlogPostContent };
